import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  Calendar,
  Copy,
  ExternalLink,
  Loader2,
  Mail,
  Minus,
  PhoneForwarded,
  PhoneOff,
  Presentation,
  SkipForward,
  Tag as TagIcon,
  ThumbsDown,
  ThumbsUp,
  User as UserIcon,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth.store";
import { useCurrentProject } from "@/hooks/useCurrentProject";
import { useDefaultTimezone } from "@/hooks/useDefaultTimezone";
import { getCompany, updateCompany } from "@/api/companies";
import { updateContact } from "@/api/contacts";
import { logAction } from "@/api/activity";
import { createTask } from "@/api/tasks";
import { addTagsToCompany, createTag, listTags } from "@/api/tags";
import { formatDate, formatEurCompact } from "@/lib/formatters";
import { formatPhone, isPhoneNumber, phoneHref } from "@/lib/phone";
import {
  getMaxNoAnswerCalls,
  getNoAnswerCooldownHours,
} from "@/lib/projectConfig";
import { combineDateTimeInTz } from "@/lib/timezones";
import { TimezoneSelect } from "@/components/shared/TimezoneSelect";
import type { CompanyDetail, EmbeddedContact } from "@/api/types";
import type {
  CallOutcome,
  CallResult,
  CallSession,
  ScheduledAction,
} from "./types";

// Flags a lead whose contact turned out not to be the right person.
// Matched case-insensitively against existing project tags, so the
// "Wrong Owner" row that already exists on some projects is reused
// rather than duplicated.
const TAG_WRONG_OWNER = "Wrong Owner";

interface ActiveSessionProps {
  slug: string;
  /** Hidden tabs stay mounted, but must not handle keyboard shortcuts. */
  active: boolean;
  session: CallSession;
  setSession: (next: CallSession | ((prev: CallSession) => CallSession)) => void;
  onEnd: () => void;
}

/** Active state: lead card with phone + financials and the four
 *  outcome buttons, always visible — the operator dials from their
 *  own phone, then logs the result directly (no Start/Stop timer).
 *  Picking an outcome updates the pipeline status, logs the activity,
 *  and optionally schedules a follow-up task. */
export function ActiveSession({
  slug,
  active,
  session,
  setSession,
  onEnd,
}: ActiveSessionProps) {
  const currentUser = useAuthStore((s) => s.user);
  const { project } = useCurrentProject();
  // Configurable per project. At `maxNoAnswer` unanswered calls the
  // backend routes the lead out of the queue — to `email_pending` if a
  // decision-maker has an email, otherwise to `unreachable`. The
  // frontend only surfaces the warning at the last-attempt tick
  // (counter === max - 1); it never writes the status itself.
  const maxNoAnswer = getMaxNoAnswerCalls(project);
  const cooldownHours = getNoAnswerCooldownHours(project);
  const current = session.queue[session.currentIndex];

  // Pull full detail for the current lead — listCompanies returns a
  // trimmed primary_contact preview (no phone / title), so we fetch
  // the detail to render the dialing card.
  const [detail, setDetail] = useState<CompanyDetail | null>(null);
  useEffect(() => {
    if (!current) return;
    let cancelled = false;
    setDetail(null);
    getCompany(slug, current.id)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
      })
      .catch(() => {
        if (cancelled) return;
        setDetail(null);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, current?.id]);

  // Pick the contact to dial: prefer the DM-flagged one, else the
  // first contact with a phone, else the first contact.
  const primary: EmbeddedContact | null = useMemo(() => {
    const contacts = detail?.contacts ?? [];
    if (contacts.length === 0) return null;
    return (
      contacts.find((c) => c.is_decision_maker && c.phone) ??
      contacts.find((c) => c.phone) ??
      contacts.find((c) => c.is_decision_maker) ??
      contacts[0]
    );
  }, [detail]);

  // Editable email for the dialed contact — sales often discover
  // wrong / missing emails during the call, so a quick blur-to-save
  // keeps the CRM clean without leaving the session. Synced from the
  // fetched detail; PATCHed via updateContact on blur.
  const [emailDraft, setEmailDraft] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);
  useEffect(() => {
    setEmailDraft(primary?.email ?? "");
  }, [primary?.id, primary?.email]);

  // Outcome form runs in two steps: step 1 picks the outcome (the
  // four buttons are always visible — no timer to gate them); step 2
  // (only for interested / neutral) schedules an intro / pitch /
  // follow-up. not_interested and no_answer auto-advance from step 1.
  // `notes` doubles as Company.notes draft AND the value the
  // post-call submission folds into the activity log payload — one
  // textarea, one source of truth, persisted via updateCompany on
  // blur. Synced from the fetched detail below.
  const [notes, setNotes] = useState("");
  const [notesSaving, setNotesSaving] = useState(false);
  // Sync the textarea with company.notes whenever the loaded detail
  // changes (new lead reached, or someone PATCHes the company from
  // elsewhere). Driven by the detail snapshot — independent of the
  // queue index so we don't blank the field before the fetch resolves.
  useEffect(() => {
    setNotes(detail?.notes ?? "");
  }, [detail?.id, detail?.notes]);
  const [selectedOutcome, setSelectedOutcome] = useState<CallOutcome | null>(
    null,
  );
  const [scheduledAction, setScheduledAction] =
    useState<ScheduledAction | null>(null);
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("10:00");
  // Default to the user's preferred timezone (read by the same hook
  // TaskCreateDialog / CallLogForm / ConfigModal use). Persists across
  // leads in the same session — operators usually schedule everything
  // in one zone per shift, so we don't reset on queue advance.
  const defaultTimezone = useDefaultTimezone();
  const [timezone, setTimezone] = useState(defaultTimezone);
  // Submitting guard — outcome buttons can't double-fire while the
  // PATCH + POST chain is in flight.
  const submittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Tag-name → id cache, so flagging several leads in one session
  // only costs a listTags on the first click.
  const tagIdsByName = useRef(new Map<string, number>());
  // Already-flagged state comes straight off the fetched detail, so it
  // resets per lead. Used to skip a redundant POST when the rep hits
  // a company that was already flagged in an earlier session.
  const hasWrongOwnerTag = (detail?.tags ?? []).some(
    (t) => t.name.toLowerCase() === TAG_WRONG_OWNER.toLowerCase(),
  );

  // Reset the form whenever the queue cursor moves. `notes` is NOT
  // reset here — it tracks Company.notes, which is sync'd by the
  // detail-driven effect above.
  useEffect(() => {
    setSelectedOutcome(null);
    setScheduledAction(null);
    setScheduledDate("");
    setScheduledTime("10:00");
  }, [session.currentIndex]);

  function advance(result: CallResult) {
    setSession((prev) => ({
      ...prev,
      currentIndex: prev.currentIndex + 1,
      results: [...prev.results, result],
    }));
  }

  /** Step 1 — outcome picked. Interested / neutral wait for the
   *  scheduling step; the two terminal outcomes submit immediately. */
  function handleSelectOutcome(outcome: CallOutcome) {
    if (submittingRef.current) return;
    setSelectedOutcome(outcome);
    if (outcome === "not_interested" || outcome === "no_answer") {
      void submitCall(outcome, null, null);
    }
  }

  /** Pipeline status mapping, keyed on the outcome + the booked
   *  follow-up action:
   *    interested + intro    → intro_planned
   *    interested + pitch    → pitch_planned
   *    interested + followup → to_be_recontacted
   *    interested (no plan)  → contact_called
   *    neutral    + followup → to_be_recontacted
   *    neutral    (no plan)  → contact_called
   *    not_interested        → refused
   *    no_answer             → null (no PATCH)
   *
   *  A booked follow-up (either outcome) MUST land on
   *  `to_be_recontacted` — that's the status the `follow_ups` call
   *  queue selects on, so the lead actually resurfaces for the
   *  recontact the operator just scheduled. `contact_called` would drop
   *  it out of every queue and the follow-up task would dangle.
   *
   *  no_answer returns null on purpose — the backend's call-log handler
   *  bumps the counter AND owns the resulting status: kept on
   *  to_be_contacted under the cap, then routed to email_pending (a
   *  decision-maker has an email) or parked in unreachable (none) once
   *  the threshold is hit. Sending a PATCH from here would race and
   *  overwrite that decision. */
  function nextStatus(
    outcome: CallOutcome,
    action: ScheduledAction | null,
  ): string | null {
    if (outcome === "interested") {
      if (action === "intro") return "intro_planned";
      if (action === "pitch") return "pitch_planned";
      if (action === "followup") return "to_be_recontacted";
      return "contact_called";
    }
    if (outcome === "neutral") {
      return action === "followup" ? "to_be_recontacted" : "contact_called";
    }
    if (outcome === "not_interested") return "refused";
    return null;
  }

  // Convert the picked date / time / timezone trio into a UTC ISO
  // the backend can store as an instant. `combineDateTimeInTz` is
  // the same helper TaskCreateDialog / CronSchedulerBuilder use, so
  // scheduling here lands on the same wall-clock the user sees on
  // those screens. Returns null only on malformed input — guarded
  // at the call site where the date/time/tz are already validated.
  function dueAtIso(date: string, time: string, tz: string): string | null {
    return combineDateTimeInTz(date, time, tz);
  }

  async function submitCall(
    outcome: CallOutcome,
    action: ScheduledAction | null,
    date: string | null,
  ) {
    if (submittingRef.current || !current) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      const trimmedNotes = notes.trim() || undefined;

      await logAction(slug, current.id, {
        action: "call_logged",
        details: {
          outcome,
          // No call timer anymore — the operator dials from their own
          // phone, so there's no measured talk time to report.
          duration_minutes: 0,
          contact_id: primary?.id ?? null,
          contact_name: primary?.full_name ?? null,
          notes: trimmedNotes,
          // A booked follow-up makes the caller its owner, server-side: the
          // follow_ups queue serves a callback to the company's owner.
          scheduled_action: action ?? undefined,
        },
      });

      const status = nextStatus(outcome, action);
      if (status && status !== current.pipeline_status) {
        await updateCompany(slug, current.id, { pipeline_status: status });
      }

      // Scheduled task — intro / pitch / explicit follow-up. Time
      // defaults to 10:00 (the scheduledTime state) when the form
      // only collects a date (neutral flow). due_at is the UTC
      // instant resolved from the picked wall-clock + tz; we also
      // ship scheduled_timezone so the backend / calendar exports
      // render the wall-clock back in the same zone the user picked.
      if (action && date && currentUser) {
        const taskConfig: Record<
          ScheduledAction,
          { title: string; task_type: "call" | "meeting" }
        > = {
          intro: { title: `Intro: ${current.name}`, task_type: "meeting" },
          pitch: { title: `Pitch: ${current.name}`, task_type: "meeting" },
          followup: {
            title: `Follow-up: ${current.name}`,
            task_type: "call",
          },
        };
        const dueAt = dueAtIso(date, scheduledTime, timezone);
        if (dueAt) {
          await createTask(slug, {
            ...taskConfig[action],
            company_id: current.id,
            contact_id: primary?.id ?? null,
            assigned_to_id: currentUser.id,
            due_at: dueAt,
            scheduled_timezone: timezone,
            description: trimmedNotes,
          });
        }
      }

      // No-answer follow-up tasks were created here in 3-day chunks
      // — that logic moved server-side: the backend bumps the
      // no-answer counter and, at the max_no_answer_calls threshold,
      // routes the lead to email_pending or unreachable. Under the
      // threshold it stays in the call rotation (behind the retry
      // cooldown); no per-call task is needed anymore.

      advance({
        companyId: current.id,
        companyName: current.name,
        outcome,
        notes: trimmedNotes,
        scheduledAction: action ?? undefined,
        timestamp: Date.now(),
      });
    } catch (err) {
      toast.error("Couldn't save the call, try again");
      // Roll back the outcome selection so the operator can retry —
      // otherwise the form stays stuck on a step they can't redo.
      setSelectedOutcome(null);
       
      console.warn("[CallCenter] outcome submit failed", err);
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }

  /** Resolve a tag name to an id — cache, then listTags (case
   *  -insensitive, so an existing "Wrong Owner" row is reused), then
   *  createTag for projects that don't have it yet. Mirrors the
   *  resolver in EnrichmentWork. */
  async function resolveTagId(name: string): Promise<number | null> {
    const key = name.toLowerCase();
    const cached = tagIdsByName.current.get(key);
    if (cached != null) return cached;
    try {
      const all = await listTags(slug);
      const hit = all.find((t) => t.name.toLowerCase() === key);
      const id = hit ? hit.id : (await createTag(slug, { name })).id;
      tagIdsByName.current.set(key, id);
      return id;
    } catch (err) {
      console.warn("[CallCenter] tag resolve failed", name, err);
      return null;
    }
  }

  /** Tags the lead as wrong owner, drops it to `refused`, then moves
   *  on. The status change mirrors handleRefuse (guarded so we don't
   *  bump pipeline_status_changed_at when it's already there). No
   *  activity log. The session row records `refused` — CallOutcome has
   *  no wrong-data value and the backend aggregates those wire values,
   *  so a dedicated one would need a backend change. Re-tagging is
   *  skipped when the flag is already there; the queue advances
   *  either way. */
  async function handleWrongData() {
    if (!current || submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      if (!hasWrongOwnerTag) {
        const tagId = await resolveTagId(TAG_WRONG_OWNER);
        if (tagId == null) {
          toast.error("Couldn't tag the company, try again");
          return;
        }
        await addTagsToCompany(slug, current.id, [tagId]);
      }
      if (current.pipeline_status !== "refused") {
        await updateCompany(slug, current.id, { pipeline_status: "refused" });
      }
      toast.success(`Tagged "${TAG_WRONG_OWNER}", marked refused`);
      advance({
        companyId: current.id,
        companyName: current.name,
        outcome: "refused",
        timestamp: Date.now(),
      });
    } catch (err) {
      toast.error("Couldn't tag the company, try again");
      console.warn("[CallCenter] wrong-data tag failed", err);
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }

  async function saveEmail() {
    if (!primary) return;
    const next = emailDraft.trim();
    const prev = primary.email ?? "";
    if (next === prev) return;
    setEmailSaving(true);
    try {
      await updateContact(slug, primary.id, { email: next || null });
      // Merge the new value into the detail snapshot so subsequent
      // renders (and `primary` re-derivation) reflect the save without
      // a full refetch.
      setDetail((d) =>
        d
          ? {
              ...d,
              contacts: d.contacts.map((c) =>
                c.id === primary.id ? { ...c, email: next || null } : c,
              ),
            }
          : d,
      );
      toast.success("Email updated");
    } catch (err) {
      toast.error("Couldn't update email, try again");
      setEmailDraft(prev);
      console.warn("[CallCenter] email update failed", err);
    } finally {
      setEmailSaving(false);
    }
  }

  async function saveNotes() {
    if (!current || !detail) return;
    const next = notes;
    const prev = detail.notes ?? "";
    if (next === prev) return;
    setNotesSaving(true);
    try {
      const updated = await updateCompany(slug, current.id, {
        notes: next.trim() || null,
      });
      setDetail((d) => (d ? { ...d, notes: updated.notes ?? null } : d));
    } catch (err) {
      toast.error("Couldn't save notes, try again");
      setNotes(prev);
      console.warn("[CallCenter] notes update failed", err);
    } finally {
      setNotesSaving(false);
    }
  }

  function handleSkip() {
    if (!current) return;
    advance({
      companyId: current.id,
      companyName: current.name,
      outcome: "skipped",
      timestamp: Date.now(),
    });
  }

  async function handleRefuse() {
    if (!current || submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      if (current.pipeline_status !== "refused") {
        await updateCompany(slug, current.id, { pipeline_status: "refused" });
      }
      advance({
        companyId: current.id,
        companyName: current.name,
        outcome: "refused",
        timestamp: Date.now(),
      });
    } catch (err) {
      toast.error("Couldn't mark refused, try again");
       
      console.warn("[CallCenter] refuse failed", err);
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }

  // Keyboard shortcuts only run while the session tab is visible, and
  // ignore inputs, textareas and contenteditable elements.
  // Outcome shortcuts (1-5) and skip / refuse (S / R) fire at step 1
  // — once an outcome is picked, step 2 (scheduling) uses the buttons.
  useEffect(() => {
    if (!active) return;
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable
      ) {
        return;
      }
      if (selectedOutcome) return;
      if (e.key === "1") handleSelectOutcome("interested");
      else if (e.key === "2") handleSelectOutcome("neutral");
      else if (e.key === "3") handleSelectOutcome("not_interested");
      else if (e.key === "4") handleSelectOutcome("no_answer");
      else if (e.key === "5") void handleWrongData();
      else if (e.key === "s" || e.key === "S") handleSkip();
      else if (e.key === "r" || e.key === "R") void handleRefuse();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, session.currentIndex, selectedOutcome, current?.id]);

  // Once the queue is exhausted, hand control back to the parent so
  // it can flip into the Summary state.
  useEffect(() => {
    if (session.currentIndex >= session.queue.length) onEnd();
  }, [session.currentIndex, session.queue.length, onEnd]);

  if (!current) return null;
  const meta = (current.metadata ?? {}) as Record<string, unknown>;
  // Unanswered calls in the current cycle. Read from the first-class
  // `no_answer_attempts` the backend projects onto every CompanyRead —
  // the old `metadata.no_answer_count` read is the same number, but only
  // the projection is guaranteed present and reset on reactivation.
  const noAnswerCount = Math.max(0, Math.floor(current.no_answer_attempts ?? 0));
  const isLastAttempt = noAnswerCount === maxNoAnswer - 1;
  // Where the NEXT unanswered call sends this lead. Mirrors the backend
  // rule: a decision-maker with an email routes to `email_pending`,
  // otherwise the lead is parked in `unreachable`. Computed from the
  // fetched detail (same set the backend queries); null while the detail
  // is still loading, so the banner stays silent rather than guessing.
  const dmHasEmail: boolean | null = detail
    ? (detail.contacts ?? []).some(
        (c) => c.is_decision_maker && (c.email ?? "").trim().length > 0,
      )
    : null;
  const lastNoAnswerDate = current.last_no_answer_at
    ? new Date(current.last_no_answer_at)
    : null;
  const lastNoAnswerValid =
    lastNoAnswerDate && !Number.isNaN(lastNoAnswerDate.getTime())
      ? lastNoAnswerDate
      : null;
  // The retry queue hides a lead for `no_answer_cooldown_hours` after an
  // unanswered call. Saying so on the card is what stops "why did the
  // company I just called vanish from the queue?" being read as a bug.
  const cooldownEndsAt = lastNoAnswerValid
    ? new Date(lastNoAnswerValid.getTime() + cooldownHours * 3_600_000)
    : null;
  const inCooldown = cooldownEndsAt ? cooldownEndsAt.getTime() > Date.now() : false;
  const contactMeta = (primary?.metadata ?? primary?.extra_metadata ?? {}) as Record<
    string,
    unknown
  >;
  // The dirigeant's number when it is one; otherwise the company's. A
  // placeholder left by an old import ("1") must not hide the company line.
  const contactPhone = primary?.phone;
  const phone = isPhoneNumber(contactPhone) ? contactPhone : current.main_phone;
  const interestedCount = session.results.filter(
    (r) => r.outcome === "interested",
  ).length;
  const skippedCount = session.results.filter(
    (r) => r.outcome === "skipped",
  ).length;
  const progress = session.currentIndex / session.queue.length;
  const queueAhead = session.queue.slice(
    session.currentIndex + 1,
    session.currentIndex + 4,
  );

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onEnd}
          className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="h-4 w-4" /> End session
        </button>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-slate-500">
            {session.currentIndex + 1} / {session.queue.length}
          </span>
          <span className="font-medium text-emerald-600">
            {interestedCount} interested
          </span>
          <span className="text-slate-400">{skippedCount} skipped</span>
        </div>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full bg-indigo-500 transition-all duration-500"
          style={{ width: `${progress * 100}%` }}
        />
      </div>

      <div className="space-y-4 rounded-plane bg-white p-6 shadow-plane">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-xl font-bold text-slate-900">
                {current.name}
              </h2>
              {/* Pappers link — only meaningful for French SIRENs.
               *  Mirrors the inline link used on the Enrichment page so
               *  operators can pull up the legal record in one click
               *  during a call. */}
              {current.tax_id && current.tax_id_type === "siren" && (
                <a
                  href={`https://www.pappers.fr/entreprise/${current.tax_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:underline"
                >
                  <ExternalLink className="h-3 w-3" />
                  Pappers
                </a>
              )}
              {/* Compact counter next to the name; the full history
               *  (attempts, last date, cooldown) is the banner below,
               *  which is what the operator reads before dialing. */}
              {noAnswerCount > 0 && (
                <span className="rounded-full bg-orange-50 px-2 py-0.5 text-xs text-orange-600">
                  {/* The "/max" fraction only means something below the
                    *  cap. Legacy rows carry counts above it (they
                    *  predate the current threshold), and "7/5" reads
                    *  like a bug — show the bare count there. */}
                  📞 {noAnswerCount}
                  {noAnswerCount < maxNoAnswer ? `/${maxNoAnswer}` : ""} no
                  answer
                </span>
              )}
            </div>
            <p className="truncate text-sm text-slate-500">
              {[
                current.sector,
                [current.city, current.postal_code].filter(Boolean).join(" "),
                current.legal_form,
                current.headcount != null
                  ? `${current.headcount} emp.`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-slate-900">
              {current.effective_score ?? current.score ?? "—"}
            </div>
            <div className="text-[10px] uppercase text-slate-400">Score</div>
          </div>
        </div>

        {/* Call history, read BEFORE dialing. A lead that never picked
          *  up is a different conversation from a fresh one, and the
          *  cooldown line explains why a company called this morning is
          *  no longer in the retry queue. Silent at zero attempts. */}
        {noAnswerCount > 0 && (
          <div
            className={cn(
              "flex items-start gap-3 rounded-xl border px-4 py-3",
              isLastAttempt
                ? "border-rose-200 bg-rose-50"
                : "border-amber-200 bg-amber-50",
            )}
          >
            <AlertTriangle
              className={cn(
                "mt-0.5 h-5 w-5 shrink-0",
                isLastAttempt ? "text-rose-600" : "text-amber-600",
              )}
            />
            <div className="min-w-0 space-y-1">
              <p
                className={cn(
                  "text-sm font-semibold",
                  isLastAttempt ? "text-rose-900" : "text-amber-900",
                )}
              >
                {noAnswerCount} unanswered{" "}
                {noAnswerCount === 1 ? "call" : "calls"} so far
                {lastNoAnswerValid && (
                  <>, last on {formatDate(lastNoAnswerValid)}</>
                )}
              </p>
              {isLastAttempt && (
                <p className="text-xs text-rose-800">
                  {dmHasEmail === null
                    ? "One more unanswered call takes this lead out of the call queue."
                    : dmHasEmail
                      ? "One more unanswered call hands this lead to the email sequence, because a decision-maker has an email."
                      : "One more unanswered call parks this lead as Unreachable, since there is no decision-maker email to fall back on."}
                </p>
              )}
              {inCooldown && cooldownEndsAt && (
                <p
                  className={cn(
                    "text-xs",
                    isLastAttempt ? "text-rose-800" : "text-amber-800",
                  )}
                >
                  Tried in the last {cooldownHours}h, so it stays out of the No
                  Answer queue until {formatDate(cooldownEndsAt, "MMM d, HH:mm")}.
                </p>
              )}
            </div>
          </div>
        )}

        {primary && (
          <div className="rounded-xl bg-slate-50 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              {/* flex-1 so the left column actually grows to fill the
               *  space the phone/Start-Call cluster leaves on the
               *  right — otherwise the editable email input is
               *  capped at its intrinsic width and reads tiny. */}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <UserIcon className="h-4 w-4 text-slate-400" />
                  <span className="font-semibold text-slate-800">
                    {primary.full_name}
                  </span>
                  {primary.title && (
                    <span className="text-xs text-slate-500">
                      · {primary.title}
                    </span>
                  )}
                </div>
                {(typeof contactMeta.age === "number" ||
                  typeof contactMeta.tenure_years === "number") && (
                  <div className="ml-6 mt-1 flex items-center gap-3 text-xs">
                    {typeof contactMeta.age === "number" && (
                      <span
                        className={cn(
                          contactMeta.age >= 65
                            ? "font-medium text-emerald-600"
                            : "text-slate-500",
                        )}
                      >
                        {contactMeta.age} ans
                      </span>
                    )}
                    {typeof contactMeta.tenure_years === "number" && (
                      <span className="text-slate-500">
                        {contactMeta.tenure_years} ans tenure
                      </span>
                    )}
                  </div>
                )}
                {/* Editable email — sales correct wrong / missing
                 *  emails during the call. Save on blur (or Enter)
                 *  via updateContact; the detail snapshot is patched
                 *  optimistically so the input doesn't snap back. */}
                <div className="ml-6 mt-1 flex items-center gap-2">
                  <Mail className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                  <Input
                    type="email"
                    value={emailDraft}
                    placeholder="Email…"
                    disabled={emailSaving}
                    onChange={(e) => setEmailDraft(e.target.value)}
                    onBlur={() => void saveEmail()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        (e.target as HTMLInputElement).blur();
                      }
                    }}
                    className="h-7 w-full min-w-0 max-w-xs flex-1 border-slate-200 bg-white px-2 text-xs"
                  />
                </div>
              </div>

              {/* Phone number is its own tel: link — the operator
               *  taps it (or Copy) to dial, then logs the result with
               *  the outcome buttons below. No Start/Stop timer. */}
              {isPhoneNumber(phone) && (
                <div className="flex items-center gap-2">
                  <a
                    href={phoneHref(phone) ?? undefined}
                    className="font-mono text-lg font-bold text-indigo-600"
                  >
                    {formatPhone(phone)}
                  </a>
                  <button
                    type="button"
                    onClick={() => {
                      void navigator.clipboard.writeText(formatPhone(phone));
                      toast.success("Copied");
                    }}
                    title="Copy"
                    className="rounded-lg p-1.5 hover:bg-slate-200"
                  >
                    <Copy className="h-4 w-4 text-slate-400" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
          {typeof meta.revenue_euros === "number" && (
            <span>
              CA{" "}
              <strong className="text-slate-900">
                {formatEurCompact(meta.revenue_euros)}
              </strong>
            </span>
          )}
          {typeof meta.ebitda_euros === "number" && (
            <span>
              EBITDA{" "}
              <strong className="text-slate-900">
                {formatEurCompact(meta.ebitda_euros)}
              </strong>
            </span>
          )}
          {typeof meta.ebitda_margin_percent === "number" && (
            <span>
              Margin{" "}
              <strong className="text-slate-900">
                {meta.ebitda_margin_percent.toFixed(1)}%
              </strong>
            </span>
          )}
          {typeof meta.debt_leverage_ratio === "number" && (
            <span>
              Leverage{" "}
              <strong className="text-slate-900">
                {meta.debt_leverage_ratio.toFixed(2)}x
              </strong>
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500">
          <span>
            Finance{" "}
            <strong className="text-slate-700">
              {(meta.finance_score as number | undefined) ?? 0}/50
            </strong>
          </span>
          <span>
            Governance{" "}
            <strong className="text-slate-700">
              {(meta.governance_score as number | undefined) ?? 0}/45
            </strong>
          </span>
          <span>
            Market{" "}
            <strong className="text-slate-700">
              {(meta.market_score as number | undefined) ?? 0}/5
            </strong>
          </span>
        </div>

        {/* Permanent notes textarea — single source of truth bound to
         *  Company.notes. Sits between the contact card / financials
         *  and the outcome buttons / Skip / Refuse footer. Persists on
         *  blur via updateCompany; the outcome form does NOT re-render
         *  its own textarea. */}
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => void saveNotes()}
          placeholder="Notes sur ce prospect..."
          disabled={notesSaving}
          className="min-h-[80px] max-h-[200px] w-full resize-none overflow-y-auto rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
        />

        {/* Outcome buttons — always visible. The operator dials, then
         *  logs the result here directly. Step 1 is the four-button
         *  grid; picking interested / neutral reveals the step-2
         *  scheduling block. */}
        <div className="space-y-4 border-t border-slate-900/[0.06] pt-4">
          {!selectedOutcome && (
            <div className="grid grid-cols-5 gap-2">
              <OutcomeStep1Button
                onClick={() => handleSelectOutcome("interested")}
                disabled={isSubmitting}
                tone="emerald"
                label="Interested"
                shortcut="1"
                icon={<ThumbsUp className="h-5 w-5 text-emerald-600" />}
              />
              <OutcomeStep1Button
                onClick={() => handleSelectOutcome("neutral")}
                disabled={isSubmitting}
                tone="amber"
                label="Neutral"
                shortcut="2"
                icon={<Minus className="h-5 w-5 text-amber-600" />}
              />
              <OutcomeStep1Button
                onClick={() => handleSelectOutcome("not_interested")}
                disabled={isSubmitting}
                tone="rose"
                label="Not Interested"
                shortcut="3"
                icon={<ThumbsDown className="h-5 w-5 text-rose-600" />}
              />
              <OutcomeStep1Button
                onClick={() => handleSelectOutcome("no_answer")}
                disabled={isSubmitting}
                tone="slate"
                label="No Answer"
                shortcut="4"
                icon={<PhoneOff className="h-5 w-5 text-slate-500" />}
              />
              <OutcomeStep1Button
                onClick={() => void handleWrongData()}
                disabled={isSubmitting}
                tone="rose"
                label="Wrong data"
                shortcut="5"
                icon={<TagIcon className="h-5 w-5 text-rose-600" />}
              />
            </div>
          )}

          {selectedOutcome === "interested" && (
            <div className="space-y-3 duration-200 animate-in slide-in-from-bottom-2">
              <div className="flex items-center gap-2">
                <span className="flex h-6 items-center rounded-full bg-emerald-100 px-2 text-xs font-medium text-emerald-700">
                  ✓ Interested
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedOutcome(null)}
                  className="text-xs text-slate-400 hover:text-slate-600"
                >
                  Change
                </button>
              </div>

              <p className="text-sm text-slate-600">What&rsquo;s the next step?</p>

              <div className="grid grid-cols-3 gap-2">
                <ScheduleOptionButton
                  active={scheduledAction === "intro"}
                  onClick={() =>
                    setScheduledAction(
                      scheduledAction === "intro" ? null : "intro",
                    )
                  }
                  tone="indigo"
                  icon={<Calendar className="h-5 w-5 text-indigo-500" />}
                  label="Schedule Intro"
                />
                <ScheduleOptionButton
                  active={scheduledAction === "pitch"}
                  onClick={() =>
                    setScheduledAction(
                      scheduledAction === "pitch" ? null : "pitch",
                    )
                  }
                  tone="violet"
                  icon={<Presentation className="h-5 w-5 text-violet-500" />}
                  label="Schedule Pitch"
                />
                <ScheduleOptionButton
                  active={scheduledAction === "followup"}
                  onClick={() =>
                    setScheduledAction(
                      scheduledAction === "followup" ? null : "followup",
                    )
                  }
                  tone="amber"
                  icon={
                    <PhoneForwarded className="h-5 w-5 text-amber-500" />
                  }
                  label="Follow-up Call"
                />
              </div>

              {scheduledAction && (
                <div className="flex flex-wrap items-center gap-3 duration-200 animate-in slide-in-from-top-2">
                  <Calendar className="h-4 w-4 text-slate-400" />
                  <Input
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    className="h-9 w-44 text-sm"
                    min={new Date().toISOString().split("T")[0]}
                  />
                  <Input
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    className="h-9 w-28 text-sm"
                  />
                  <TimezoneSelect
                    value={timezone}
                    onValueChange={setTimezone}
                    className="h-9 w-44 text-sm"
                  />
                </div>
              )}

              <Button
                onClick={() =>
                  void submitCall(
                    "interested",
                    scheduledAction,
                    scheduledDate || null,
                  )
                }
                disabled={
                  isSubmitting || (!!scheduledAction && !scheduledDate)
                }
                className="w-full"
                size="lg"
              >
                {isSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {scheduledAction
                  ? `Confirm & Schedule ${
                      scheduledAction === "intro"
                        ? "Intro"
                        : scheduledAction === "pitch"
                          ? "Pitch"
                          : "Follow-up"
                    }`
                  : "Continue without scheduling"}
              </Button>
            </div>
          )}

          {selectedOutcome === "neutral" && (
            <div className="space-y-3 duration-200 animate-in slide-in-from-bottom-2">
              <div className="flex items-center gap-2">
                <span className="flex h-6 items-center rounded-full bg-amber-100 px-2 text-xs font-medium text-amber-700">
                  ~ Neutral
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedOutcome(null)}
                  className="text-xs text-slate-400 hover:text-slate-600"
                >
                  Change
                </button>
              </div>

              <div className="flex items-center gap-3">
                <Switch
                  checked={scheduledAction === "followup"}
                  onCheckedChange={(v) =>
                    setScheduledAction(v ? "followup" : null)
                  }
                />
                <span className="text-sm text-slate-600">
                  Schedule a follow-up call
                </span>
              </div>

              {scheduledAction === "followup" && (
                <div className="flex flex-wrap items-center gap-3 duration-200 animate-in slide-in-from-top-2">
                  <Calendar className="h-4 w-4 text-slate-400" />
                  <Input
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    className="h-9 w-44 text-sm"
                    min={new Date().toISOString().split("T")[0]}
                  />
                  {/* The neutral flow doesn't expose a time picker
                   *  (defaults to 10:00 via scheduledTime state), but
                   *  "10:00 Paris" ≠ "10:00 NY" so the tz selector
                   *  still belongs here. */}
                  <TimezoneSelect
                    value={timezone}
                    onValueChange={setTimezone}
                    className="h-9 w-44 text-sm"
                  />
                </div>
              )}

              <Button
                onClick={() =>
                  void submitCall(
                    "neutral",
                    scheduledAction,
                    scheduledDate || null,
                  )
                }
                disabled={
                  isSubmitting ||
                  (scheduledAction === "followup" && !scheduledDate)
                }
                className="w-full"
              >
                {isSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {scheduledAction === "followup"
                  ? "Confirm & Schedule Follow-up"
                  : "Continue"}
              </Button>
            </div>
          )}
        </div>

        {!selectedOutcome && (
          <div className="flex items-center justify-between border-t border-slate-900/[0.06] pt-2">
            <button
              type="button"
              onClick={handleSkip}
              className="flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm text-slate-500 transition-all hover:bg-slate-100 hover:text-slate-700"
            >
              <SkipForward className="h-4 w-4" />
              Skip
              <kbd className="ml-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-400">
                S
              </kbd>
            </button>
            <button
              type="button"
              onClick={() => void handleRefuse()}
              disabled={isSubmitting}
              className="flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm text-rose-500 transition-all hover:bg-rose-50 hover:text-rose-700 disabled:opacity-50"
            >
              <Ban className="h-4 w-4" />
              Refuse
              <kbd className="ml-1 rounded bg-rose-50 px-1.5 py-0.5 text-[10px] text-rose-400">
                R
              </kbd>
            </button>
          </div>
        )}
      </div>

      {queueAhead.length > 0 && (
        <div className="rounded-xl border border-slate-900/[0.06] bg-slate-50/50 p-3">
          <div className="mb-2 text-[10px] font-medium uppercase text-slate-400">
            Next up
          </div>
          {queueAhead.map((c) => (
            <div
              key={c.id}
              className="flex items-center justify-between py-1.5 text-sm"
            >
              <span className="text-slate-600">{c.name}</span>
              <div className="flex items-center gap-3 text-xs text-slate-400">
                <span>{c.city}</span>
                <span className="font-mono font-medium text-slate-600">
                  {c.effective_score ?? c.score ?? "—"}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const STEP1_TONE: Record<string, string> = {
  emerald:
    "border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700",
  amber: "border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-700",
  rose: "border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-700",
  slate: "border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-600",
};
const STEP1_KBD: Record<string, string> = {
  emerald: "bg-emerald-100 text-emerald-400",
  amber: "bg-amber-100 text-amber-400",
  rose: "bg-rose-100 text-rose-400",
  slate: "bg-slate-100 text-slate-400",
};

function OutcomeStep1Button({
  onClick,
  disabled,
  tone,
  label,
  shortcut,
  icon,
}: {
  onClick: () => void;
  disabled?: boolean;
  tone: "emerald" | "amber" | "rose" | "slate";
  label: string;
  shortcut: string;
  icon: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex flex-col items-center gap-1 rounded-xl border-2 py-3 transition-all disabled:cursor-progress disabled:opacity-60",
        STEP1_TONE[tone],
      )}
    >
      {icon}
      <span className="text-xs font-semibold">{label}</span>
      <kbd className={cn("rounded px-1 text-[9px]", STEP1_KBD[tone])}>
        {shortcut}
      </kbd>
    </button>
  );
}

const SCHEDULE_ACTIVE: Record<string, string> = {
  indigo: "border-indigo-500 bg-indigo-50",
  violet: "border-violet-500 bg-violet-50",
  amber: "border-amber-500 bg-amber-50",
};
const SCHEDULE_IDLE: Record<string, string> = {
  indigo: "border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50",
  violet: "border-slate-200 hover:border-violet-300 hover:bg-violet-50/50",
  amber: "border-slate-200 hover:border-amber-300 hover:bg-amber-50/50",
};
const SCHEDULE_LABEL: Record<string, string> = {
  indigo: "text-indigo-700",
  violet: "text-violet-700",
  amber: "text-amber-700",
};

function ScheduleOptionButton({
  active,
  onClick,
  tone,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  tone: "indigo" | "violet" | "amber";
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex flex-col items-center gap-1 rounded-xl border-2 py-3 transition-all",
        active ? SCHEDULE_ACTIVE[tone] : SCHEDULE_IDLE[tone],
      )}
    >
      {icon}
      <span className={cn("text-xs font-semibold", SCHEDULE_LABEL[tone])}>
        {label}
      </span>
    </button>
  );
}

